import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    logger,
    TeacherSchoolComponent,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    getFieldValidationErrors,
    isFormErrored,
    isFormFilled,
    VerificationService,
    TeacherPersonalInfoViewModel,
    FieldValidationErrors,
    Organization,
    ExtendedFieldValidationErrors,
    updateFieldValidationErrorsByFieldId,
} from '@sheerid/jslib';

import { LogoHeaderComponent } from './LogoHeaderComponent';
import { PostalCodeComponent } from './PostalCodeComponent';
import { MarketConsentWrapperComponent as MarketConsentWrapper } from './FormFields/MarketConsentWrapper/MarketConsentWrapperComponent';


interface StepTeacherPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

const StepTeacherPersonalInfo = ({ intl, verificationService }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;

    const updateViewModel = (key, value) => {
        const nextState = produce(viewModel, (draft) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateViewModelOrganization = (choice: Organization) => {
        const nextState = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.organization.id = choice.id;
            draft.organization.name = choice.name;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateFieldValidationErrors = (fieldValidationErrors: FieldValidationErrors) => {
        verificationService.updateFieldValidationErrors(fieldValidationErrors);
    };

    const submitForm = () => {
        const currentFieldValidationErrors = getFieldValidationErrors(viewModel);
        const isFormValid = !isFormErrored(currentFieldValidationErrors);

        if (isFormValid) {
            const nextState = produce(viewModel, () => {});
            logger.info('StepTeacherPersonalInfoComponent submitting form');
            verificationService.submitStep('collectTeacherPersonalInfo', nextState, verificationService.verificationResponse);
        } else {
            updateFieldValidationErrors(currentFieldValidationErrors);
        }
};

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">

            <div className="sid-header">
                <LogoHeaderComponent/>
                <div className="sid-header__subtitle sid-l-space-top-lg">
                    <FormattedHTMLMessage id="step.personalInfo.subtitle"
                        defaultMessage="Confirm your eligibility by simply completing the form below." />
                </div>
                <div className="sid-header__title sid-l-space-top-lg">
                    <h1>
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                    </h1>
                </div>
            </div>
            <div className="sid-l-space-top-lg">
                <FormattedHTMLMessage id="step.personalInfo.requiredFields" defaultMessage="All fields are required." />
            </div>
            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                isErrored={!!fieldValidationErrors.organization}
                verificationService = { verificationService }
                placeholder={
                    intl.formatMessage({
                        id: "organiztion",
                        defaultMessage: "School",
                        })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: "firstName",
                            defaultMessage: "First Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: "lastName",
                            defaultMessage: "Last Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: "emailAddress",
                        defaultMessage: "Email Address",
                        })
                }
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Must be your school-issued email address"
                    />
                }
                onChange={
                    (newValue) => {
                        updateViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                placeholder={
                    intl.formatMessage({
                        id: "postalCode",
                        defaultMessage: "Home Zip Code",
                        })
                }
                onChange={(newValue: any) => {
                    updateViewModel(
                        'metadata',
                        {
                            postalCode: newValue,
                        },
                    );
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.hasOwnProperty('metadata') ? viewModel.metadata.postalCode : undefined}
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateViewModel("metadata", {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        onClick={submitForm}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                    >
                        <FormattedHTMLMessage id="verifyAndContinue" defaultMessage="VERIFY AND CONTINUE" />
                    </button>
                </div>
            </div>

            {/* TODO what's the proper footer text for teacher? */}
            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="teacherInfoShared"
                    values={{
                        companyName: verificationService.programTheme.intl.messages.companyName || '{Company}',
                    }}
                    defaultMessage={'Information entered into this web form will be used for verification purposes.<br>The information will be shared with Vineyard Vines.'}
                />
            </FormFooter>
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
